import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth } from '../../context/auth-context';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Loader2, RefreshCw, Package } from 'lucide-react';
import { Agent } from '../../types/agent-types-new';
import NewAgentCard from '../../components/agents/NewAgentCard';
import StatCard from '../../components/agents/StatCard';
import { useToast } from '../../components/ui/use-toast';
import { Link, useNavigate } from 'react-router-dom';
import { agentService } from '../../lib/api/agent-service';
import { AGENT_ORDER, AGENT_ID_MAPPING } from '../../lib/api/agent-order-config';
import { AGENT_NAME_TO_TYPE } from '../../lib/api/agent-name-mapping';
import { useOnboardingTracking } from '@/hooks/useOnboardingTracking';

// Get API URL from localStorage if available, otherwise use the environment variable
const getApiUrl = () => {
  const savedApiUrl = localStorage.getItem('api_url');
  return savedApiUrl || import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
};

const API_URL = getApiUrl();

/**
 * Dashboard stats computed from the agent rows themselves. Everything here is counted from
 * the rows and nothing is estimated (task-hzmpfs8oo1).
 *
 * Two fields were REMOVED rather than fixed, because neither had a source:
 *   totalRequests = activeAgents.length * 1000 + Math.floor(Math.random() * 5000)
 *     -- a random number, different on every render, labelled "Total Requests".
 *   weeklyChange  = '+12%'
 *     -- a fixed string, rendered as a measured trend next to it.
 * The executions figure now comes from agent_logs via GET /api/logs/stats/summary, and no
 * trend is shown at all: there is no recorded prior period to compare against, so any arrow
 * would be an invention. Invariant: no surface renders a value that was not measured.
 */
export const calculateDashboardStats = (agents: Agent[] = []) => {
  if (!agents || agents.length === 0) {
    return {
      totalAgents: 0,
      activeAgents: 0,
      averagePerformance: null as number | null,
    };
  }

  const activeAgents = agents.filter(agent => agent.status === 'active');
  // No source records progress (PR #6323): the mean is over the rows that carry one,
  // and null when none does, never NaN from summing undefined.
  const recordedProgress = agents.map((agent) => agent.progress).filter((p): p is number => typeof p === 'number' && Number.isFinite(p));
  const avgPerformance = recordedProgress.length > 0 ? recordedProgress.reduce((sum, p) => sum + p, 0) / recordedProgress.length : null;
  
  return {
    totalAgents: agents.length,
    activeAgents: activeAgents.length,
    averagePerformance: avgPerformance === null ? null : Math.round(avgPerformance),
  };
};

const Dashboard: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { token, isAuthenticated, loading: authLoading } = useAuth();
  const { trackOnboardingStep } = useOnboardingTracking();
  const [selectedMaturity, setSelectedMaturity] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [dashboardStats, setDashboardStats] = useState<ReturnType<typeof calculateDashboardStats>>({
    totalAgents: 0,
    activeAgents: 0,
    averagePerformance: null,
  });
  
  // Set up axios auth header when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Track "Visit agents" onboarding step when component mounts
  useEffect(() => {
    trackOnboardingStep('visit_agents', { timestamp: new Date().toISOString() });
  }, [trackOnboardingStep]);
  
  // State for showing marketplace agents
  const [showMarketplace, setShowMarketplace] = useState(true);
  
  // Fetch agents using our new agent service
  const { 
    data: agents,
    isLoading, 
    isError, 
    refetch 
  } = useQuery({
    enabled: isAuthenticated,
    queryKey: ['agents', showMarketplace],
    queryFn: async () => {
      // Fetch agents from our service
      let agents = await agentService.getAgents(showMarketplace, false); // include coming soon if showMarketplace is true
      
      // Debug logging
      console.log('[Dashboard] Raw fetched agents:', agents);
      console.log('[Dashboard] Agent IDs before dedup:', agents.map(a => ({ id: a._id, type: a.agent_type, name: a.name })));
      
      // Manual deduplication and normalization
      const normalizedAgents = new Map<string, Agent>();
      const agentTypeMap = new Map<string, Agent>(); // Map agent_type to agent for deduplication
      
      agents.forEach(agent => {
        // For MongoDB IDs, use name mapping first, then agent_type
        const isMongoId = /^[0-9a-f]{24}$/i.test(agent._id);
        let key = agent._id;
        
        if (isMongoId) {
          // Try to get type from name mapping first
          const typeFromName = AGENT_NAME_TO_TYPE[agent.name];
          if (typeFromName) {
            key = typeFromName;
          } else if (agent.agent_type) {
            key = agent.agent_type;
          }
        }
        
        // Apply ID mapping to consolidate duplicates
        const normalizedKey = AGENT_ID_MAPPING[key] || key;
        
        // Skip voice-chatbot in favor of voice-assistant
        if (key === 'voice-chatbot' || key === 'voice-chatbot-agent' || 
            agent.agent_type === 'voice-chatbot' || agent.agent_type === 'voice-chatbot-agent') {
          console.log('[Dashboard] Skipping duplicate voice agent:', agent._id, 'type:', agent.agent_type);
          return;
        }
        
        // Only keep the first occurrence of each normalized key
        if (!agentTypeMap.has(normalizedKey)) {
          agentTypeMap.set(normalizedKey, agent);
          console.log('[Dashboard] Added agent:', normalizedKey, 'name:', agent.name);
        } else {
          console.log('[Dashboard] Skipping duplicate:', normalizedKey, 'existing:', agentTypeMap.get(normalizedKey)?.name);
        }
      });
      
      // Convert map to array
      agents = Array.from(agentTypeMap.values());
      
      // Sort agents according to AGENT_ORDER
      agents = agents.sort((a, b) => {
        // Try to determine agent type from various sources
        let aType = a.agent_type || a._id;
        let bType = b.agent_type || b._id;
        
        // If we have MongoDB IDs, try to map from agent name
        if (/^[0-9a-f]{24}$/i.test(a._id)) {
          aType = AGENT_NAME_TO_TYPE[a.name] || a.agent_type || a._id;
        }
        if (/^[0-9a-f]{24}$/i.test(b._id)) {
          bType = AGENT_NAME_TO_TYPE[b.name] || b.agent_type || b._id;
        }
        
        const indexA = AGENT_ORDER.indexOf(aType);
        const indexB = AGENT_ORDER.indexOf(bType);
        
        console.log('[Dashboard] Sorting:', a.name, 'type:', aType, 'index:', indexA);
        console.log('[Dashboard] Sorting:', b.name, 'type:', bType, 'index:', indexB);
        
        // If not in order list, put at end
        if (indexA === -1 && indexB === -1) return 0; // Keep original order for unknowns
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        
        return indexA - indexB;
      });
      
      console.log('[Dashboard] After dedup and sort:', agents.map(a => ({ id: a._id, type: a.agent_type, name: a.name })));
      console.log('[Dashboard] Final agent count:', agents.length);
      
      // Calculate dashboard stats
      const stats = calculateDashboardStats(agents);
      setDashboardStats(stats);
      
      return agents;
    }
  });
  
  /**
   * Executions actually recorded in agent_logs, measured rather than estimated:
   * GET /api/logs/stats/summary groups rows by agent_type and status with a count
   * (backend-merged/routes/logs-postgresql.js:407). An agent with no rows is simply absent
   * from the map. A failed summary is NOT rendered as zero -- zero is a measurement and a
   * failure is not, so the card says the figure is unavailable and names the error.
   */
  const {
    data: statsSummary,
    error: statsError,
    isFetching: statsFetching,
    refetch: refetchStats,
  } = useQuery({
    enabled: isAuthenticated,
    queryKey: ['agent-stats-summary'],
    queryFn: () => agentService.getAgentStatsSummary(),
  });
  const recordedExecutions = statsSummary
    ? Object.values(statsSummary).reduce((sum, s) => sum + s.executions, 0)
    : null;

  // Refresh re-reads the rows AND the measured statistics, so a failed summary can be
  // retried from the same control and the counts never outlive the list.
  const refreshAll = () => Promise.all([refetch(), refetchStats()]);

  // Filter agents by maturity and category
  const getFilteredAgents = () => {
    if (!agents) return [];
    
    let filtered = [...agents];
    
    if (selectedMaturity) {
      filtered = filtered.filter((agent: Agent) => agent.maturity === selectedMaturity);
    }
    
    if (selectedCategory) {
      filtered = filtered.filter((agent: Agent) => agent.category === selectedCategory);
    }
    
    return filtered;
  };

  const filteredAgents = getFilteredAgents();
  
  // Group agents by maturity for category view, excluding 'coming-soon' agents
  const filteredNonMarketplace = filteredAgents.filter(agent => agent.marketplaceStatus !== 'coming-soon');
  const rookieAgents = filteredNonMarketplace.filter((agent: Agent) => agent.maturity === 'Rookie');
  const seniorAgents = filteredNonMarketplace.filter((agent: Agent) => agent.maturity === 'Senior');
  const vpAgents = filteredNonMarketplace.filter((agent: Agent) => agent.maturity === 'VP');
  
  // Coming soon agents
  const comingSoonAgents = showMarketplace ? 
    filteredAgents.filter(agent => agent.marketplaceStatus === 'coming-soon') : 
    [];
  
  // Group agents by category for maturity view (excluding coming soon)
  const getAgentsByCategory = () => {
    const categories: Record<string, Agent[]> = {};
    
    filteredNonMarketplace.forEach((agent: Agent) => {
      if (!categories[agent.category]) {
        categories[agent.category] = [];
      }
      categories[agent.category].push(agent);
    });
    
    return categories;
  };
  
  // Group coming soon agents by category
  const getComingSoonByCategory = () => {
    const categories: Record<string, Agent[]> = {};
    
    comingSoonAgents.forEach((agent: Agent) => {
      if (!categories[agent.category]) {
        categories[agent.category] = [];
      }
      categories[agent.category].push(agent);
    });
    
    return categories;
  };
  
  const agentsByCategory = getAgentsByCategory();
  
  const handleViewAgent = (agent: Agent) => {
    navigate(`/agents/detail/${agent._id}`);
  };
  
  return (
    <div className="container mx-auto py-6">
      <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Agent Admin Dashboard</h1>
          <p className="text-muted-foreground mt-1">Manage and monitor your AI agents</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => refreshAll()}
            disabled={isLoading || statsFetching || authLoading}
          >
            {isLoading || statsFetching ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
          <Button 
            variant={showMarketplace ? "default" : "outline"}
            onClick={() => setShowMarketplace(!showMarketplace)}
          >
            <Package className="h-4 w-4 mr-2" />
            {showMarketplace ? "Hide Coming Soon" : "Show Coming Soon"}
          </Button>
        </div>
      </div>
      
      {/* The same shape /agents uses (pages/agents/index.tsx:196-200): a failed summary names
          its own error rather than leaving a card to imply the number is simply small. */}
      {statsError && (
        <div role="alert" className="mb-4 p-3 text-sm border border-amber-200 rounded-md bg-amber-50 text-amber-900">
          Execution statistics could not be loaded, so Recorded Executions shows none: {statsError instanceof Error ? statsError.message : String(statsError)}
        </div>
      )}
      {/* Dashboard Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard 
          title="Total Agents" 
          value={dashboardStats.totalAgents} 
        />
        {/* No trend: nothing records a prior period to compare against, and the arrow
            was hard-coded "up" with a fixed "+2" beside it (task-hzmpfs8oo1). */}
        <StatCard 
          title="Active Agents" 
          value={dashboardStats.activeAgents} 
        />
        <StatCard 
          title="Avg. Performance" 
          value={dashboardStats.averagePerformance === null ? 'not recorded' : `${dashboardStats.averagePerformance}%`}
        />
        {/* "Total Requests" was a random number and the trend beside it a fixed "+12%".
            This counts what agent_logs actually recorded, and says so in its title: these
            are agent EXECUTIONS, not HTTP requests, which is what the old label implied. */}
        <StatCard 
          title="Recorded Executions"
          value={
            statsError
              ? 'unavailable'
              : recordedExecutions === null
                ? 'loading…'
                : recordedExecutions.toLocaleString()
          }
        />
      </div>
      
      {/* Filter Controls */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Maturity</label>
          <div className="flex gap-2">
            <Button 
              variant={selectedMaturity === null ? "default" : "outline"} 
              size="sm"
              onClick={() => setSelectedMaturity(null)}
            >
              All
            </Button>
            <Button 
              variant={selectedMaturity === 'Rookie' ? "default" : "outline"} 
              size="sm"
              onClick={() => setSelectedMaturity('Rookie')}
            >
              Rookie
            </Button>
            <Button 
              variant={selectedMaturity === 'Senior' ? "default" : "outline"} 
              size="sm"
              onClick={() => setSelectedMaturity('Senior')}
            >
              Senior
            </Button>
            <Button 
              variant={selectedMaturity === 'VP' ? "default" : "outline"} 
              size="sm"
              onClick={() => setSelectedMaturity('VP')}
            >
              VP
            </Button>
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-1">Category</label>
          <div className="flex gap-2 flex-wrap">
            <Button 
              variant={selectedCategory === null ? "default" : "outline"} 
              size="sm"
              onClick={() => setSelectedCategory(null)}
            >
              All
            </Button>
            <Button 
              variant={selectedCategory === 'SEO' ? "default" : "outline"} 
              size="sm"
              onClick={() => setSelectedCategory('SEO')}
            >
              SEO
            </Button>
            <Button 
              variant={selectedCategory === 'Content' ? "default" : "outline"} 
              size="sm"
              onClick={() => setSelectedCategory('Content')}
            >
              Content
            </Button>
            <Button 
              variant={selectedCategory === 'Intent' ? "default" : "outline"} 
              size="sm"
              onClick={() => setSelectedCategory('Intent')}
            >
              Intent
            </Button>
            <Button 
              variant={selectedCategory === 'Analytics' ? "default" : "outline"} 
              size="sm"
              onClick={() => setSelectedCategory('Analytics')}
            >
              Analytics
            </Button>
            <Button 
              variant={selectedCategory === 'Support' ? "default" : "outline"} 
              size="sm"
              onClick={() => setSelectedCategory('Support')}
            >
              Support
            </Button>
            <Button 
              variant={selectedCategory === 'Research' ? "default" : "outline"} 
              size="sm"
              onClick={() => setSelectedCategory('Research')}
            >
              Research
            </Button>
            <Button 
              variant={selectedCategory === 'Usability' ? "default" : "outline"} 
              size="sm"
              onClick={() => setSelectedCategory('Usability')}
            >
              Usability
            </Button>
          </div>
        </div>
      </div>
      
      {authLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Authenticating...</span>
        </div>
      ) : isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : isError ? (
        <div className="text-center p-4 border border-red-200 rounded-md bg-red-50">
          <div className="text-red-600 font-semibold mb-2">Error loading agents.</div>
          <p className="text-sm mb-4">This could be due to an API connection issue or authentication problem.</p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Button 
              onClick={() => refetch()} 
              variant="outline"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
          
          <div className="mt-4 text-xs text-muted-foreground">
            <p>API URL: <code className="bg-muted px-1 py-0.5 rounded">{API_URL}</code></p>
            <p className="mt-1">Browser Console may have more details</p>
          </div>
        </div>
      ) : agents?.length === 0 ? (
        <div className="text-center p-8 border rounded-lg">
          <div className="text-xl font-semibold mb-2">No agents found</div>
          <p className="text-muted-foreground mb-4">Your organization doesn't have any AI agents yet.</p>
          <Button onClick={() => navigate('/agents/marketplace')}>
            <Package className="h-4 w-4 mr-2" />
            Explore Marketplace
          </Button>
        </div>
      ) : (
        <>
          <Tabs defaultValue="all" className="mt-6">
            <TabsList className="mb-4">
              <TabsTrigger value="all">All Agents</TabsTrigger>
              <TabsTrigger value="byMaturity">By Maturity</TabsTrigger>
              <TabsTrigger value="byCategory">By Category</TabsTrigger>
            </TabsList>
            
            <TabsContent value="all" className="mt-0">
              {/* Regular agents */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredAgents
                  .filter(agent => agent.marketplaceStatus !== 'coming-soon')
                  .map((agent: Agent) => (
                    <NewAgentCard
                      key={agent._id}
                      agent={agent}
                      onClick={() => handleViewAgent(agent)}
                    />
                  ))
                }
              </div>
              
              {/* Coming Soon section */}
              {showMarketplace && filteredAgents.some(agent => agent.marketplaceStatus === 'coming-soon') && (
                <div className="mt-12">
                  <div className="flex items-center mb-4">
                    <h2 className="text-xl font-bold">Coming Soon</h2>
                    <Badge variant="outline" className="ml-2">Marketplace Preview</Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredAgents
                      .filter(agent => agent.marketplaceStatus === 'coming-soon')
                      .map((agent: Agent) => (
                        <NewAgentCard
                          key={agent._id}
                          agent={agent}
                          onClick={() => handleViewAgent(agent)}
                        />
                      ))
                    }
                  </div>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="byMaturity" className="mt-0">
              {vpAgents.length > 0 && (
                <div className="mb-10">
                  <h2 className="text-xl font-bold mb-4">VP Agents</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {vpAgents.map((agent: Agent) => (
                      <NewAgentCard
                        key={agent._id}
                        agent={agent}
                        onClick={() => handleViewAgent(agent)}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {seniorAgents.length > 0 && (
                <div className="mb-10">
                  <h2 className="text-xl font-bold mb-4">Senior Agents</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {seniorAgents.map((agent: Agent) => (
                      <NewAgentCard
                        key={agent._id}
                        agent={agent}
                        onClick={() => handleViewAgent(agent)}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {rookieAgents.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold mb-4">Rookie Agents</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {rookieAgents.map((agent: Agent) => (
                      <NewAgentCard
                        key={agent._id}
                        agent={agent}
                        onClick={() => handleViewAgent(agent)}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Coming Soon section */}
              {showMarketplace && comingSoonAgents.length > 0 && (
                <div className="mt-12">
                  <div className="flex items-center mb-4">
                    <h2 className="text-xl font-bold">Coming Soon</h2>
                    <Badge variant="outline" className="ml-2">Marketplace Preview</Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {comingSoonAgents.map((agent: Agent) => (
                      <NewAgentCard
                        key={agent._id}
                        agent={agent}
                        onClick={() => handleViewAgent(agent)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="byCategory" className="mt-0">
              {/* Regular agents by category */}
              {Object.entries(agentsByCategory).map(([category, categoryAgents]) => (
                <div key={category} className="mb-10">
                  <h2 className="text-xl font-bold mb-4">{category} Agents</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {categoryAgents.map((agent: Agent) => (
                      <NewAgentCard
                        key={agent._id}
                        agent={agent}
                        onClick={() => handleViewAgent(agent)}
                      />
                    ))}
                  </div>
                </div>
              ))}
              
              {/* Coming Soon section by category */}
              {showMarketplace && comingSoonAgents.length > 0 && (
                <div className="mt-12">
                  <div className="flex items-center mb-4">
                    <h2 className="text-xl font-bold">Coming Soon</h2>
                    <Badge variant="outline" className="ml-2">Marketplace Preview</Badge>
                  </div>
                  
                  {Object.entries(getComingSoonByCategory()).map(([category, categoryAgents]) => (
                    <div key={`coming-soon-${category}`} className="mb-10">
                      <h3 className="text-lg font-medium mb-4">{category} Agents</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {categoryAgents.map((agent: Agent) => (
                          <NewAgentCard
                            key={agent._id}
                            agent={agent}
                            onClick={() => handleViewAgent(agent)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </>
      )}
      
      {/* Debug info removed - was causing error */}
    </div>
  );
};

export default Dashboard;